import { useCallback, useContext } from "react";
import { ResponseContext, TableContext } from "@swiggy-private/portal-dineout-context";
import type { ICartItems } from "@swiggy-private/portal-dineout-context";
import { useActions } from "./useActions";
import { useCrossSellActions } from "./useCrossSellActions";
import { SUCCESS_STATUS_CODE } from "../constants/api";
import { updateLocalCart } from "../utils";
import { deepClone } from "../utils/menu";

export interface ISelectedVariant {
    group_id: string;
    variation_id: string;
    name?: string;
}

type CartItemWithUUID = ICartItems & {
    menu_item_uuid: string;
    variants?: ISelectedVariant[];
};

export const useCartActions = (
    updateMenuPostCartUpdate: (updatedCartItems: ICartItems[]) => void,
    options?: { enableCrossSell?: boolean },
) => {
    const { state, dispatch } = useContext(ResponseContext);
    const { tableId, restaurantId, verifyPinFlow, setVerifyPinFlow, parentOrderId } = useContext(TableContext);
    const { handleLogin, handlePin } = useActions();
    const { handleCrossSellOnCartUpdate } = useCrossSellActions();

    const updateCartHandler = useCallback(
        (newItem: CartItemWithUUID, isCustomisable = false, isCrossSellItem = false) => {
            const { localCart } = state.cart;
            let updatedCartItems = localCart.cartItems as CartItemWithUUID[];
            // Detect if item is customizable by checking if it has addons or variants
            const hasCustomizations = !!newItem.addons?.length || !!newItem.variants?.length;
            const isCustomizableItem = isCustomisable || hasCustomizations;
            if (!isCustomizableItem) {
                updatedCartItems = updatedCartItems.map((item: CartItemWithUUID) =>
                    item.menu_item_uuid === newItem.menu_item_uuid ? newItem : item,
                );
            }
            if (isCustomizableItem) {
                // Create a deep copy to avoid mutating the original array.
                updatedCartItems = deepClone(updatedCartItems);

                // Check if item with same customization (same UUID) already exists
                const existingItemIndex = updatedCartItems.findIndex(
                    (item: CartItemWithUUID) => item.menu_item_uuid === newItem.menu_item_uuid,
                );

                if (existingItemIndex !== -1) {
                    if (isCustomisable) {
                        // Adding same customization from modal - INCREMENT quantity
                        const updatedItem = {
                            ...updatedCartItems[existingItemIndex],
                            quantity: updatedCartItems[existingItemIndex].quantity + newItem.quantity,
                        };
                        // Remove from current position and add to end so it becomes the "last added" item
                        updatedCartItems.splice(existingItemIndex, 1);
                        updatedCartItems.push(updatedItem);
                    } else {
                        // Repeat/Decrement flow - REPLACE with updated item
                        const existingItem = updatedCartItems[existingItemIndex];
                        // Check if this is an increment (new quantity > existing quantity)
                        const isIncrement = newItem.quantity > existingItem.quantity;

                        if (isIncrement) {
                            // For increment flow, move to end to become "last added" item
                            updatedCartItems.splice(existingItemIndex, 1);
                            updatedCartItems.push(newItem);
                        } else {
                            updatedCartItems[existingItemIndex] = newItem;
                            // Remove item if quantity is 0 or less
                            if (updatedCartItems[existingItemIndex].quantity <= 0) {
                                updatedCartItems.splice(existingItemIndex, 1);
                            }
                        }
                    }
                } else {
                    // New customization - add to cart
                    updatedCartItems.push(newItem);
                }
            } else if (newItem.quantity === SUCCESS_STATUS_CODE) {
                updatedCartItems = updatedCartItems.filter(
                    (item: CartItemWithUUID) => item.menu_item_uuid !== newItem.menu_item_uuid,
                );
            } else if (!updatedCartItems.includes(newItem)) {
                updatedCartItems.push(newItem);
            }

            updateMenuPostCartUpdate(updatedCartItems);

            updateLocalCart({ localCart, updatedCartItems, restaurantId, tableId, parentOrderId }, dispatch);

            const previousQuantity =
                (localCart.cartItems as CartItemWithUUID[]).find(
                    (item) => item.menu_item_uuid === newItem.menu_item_uuid,
                )?.quantity ?? 0;
            if (options?.enableCrossSell !== false) {
                const cartItemIdsAtOpen = (localCart.cartItems as CartItemWithUUID[]).map(
                    (cartItem) => cartItem.menu_item_id,
                );
                handleCrossSellOnCartUpdate(newItem, previousQuantity, { isCrossSellItem, cartItemIdsAtOpen });
            }
        },
        [
            state.cart,
            updateMenuPostCartUpdate,
            restaurantId,
            tableId,
            parentOrderId,
            dispatch,
            options?.enableCrossSell,
            handleCrossSellOnCartUpdate,
        ],
    );

    const onAddButtonClick = useCallback(
        (newItem: CartItemWithUUID, isCustomisable?: boolean, isCrossSellItem = false) => {
            if (!handleLogin() || !handlePin()) {
                return;
            }
            updateCartHandler(newItem, isCustomisable, isCrossSellItem);
        },
        [handleLogin, handlePin, updateCartHandler],
    );

    return { updateCartHandler, onAddButtonClick, verifyPinFlow, setVerifyPinFlow };
};
